/**
 * Tenants, tax owners (individuals/companies) and contractors/suppliers.
 */
import {
  ACCOUNTING_BASES,
  MTD_STATUSES,
  OWNER_TYPES,
  QUARTERLY_PERIOD_TYPES,
  TENANCY_STATUSES,
} from "@shared/constants.ts";
import { contractorSchema, taxOwnerChecks, taxOwnerSchema, tenantSchema } from "@shared/schemas/portfolio.ts";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  CheckboxField,
  DateField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { FilterBar, SearchInput } from "../../components/ui/Filters.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { percent, useFormat } from "../../lib/format.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useSortState, useUrlState } from "../../lib/url-state.ts";
import { ExpenseTable } from "../money/ExpensePages.tsx";
import { CommunicationTable } from "../operations/CommunicationPages.tsx";
import { MaintenanceTable } from "../operations/MaintenancePages.tsx";

const nullsToEmpty = (r: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]));

// ------------------------------------------------------------------ tenants

interface TenantRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  notes: string;
  tenancyCount: number;
  tenancies?: Array<{
    id: string;
    startDate: string;
    endDate: string | null;
    status: string;
    propertyId: string;
    addressLine1: string;
    postcode: string;
  }>;
}

export function TenantListPage() {
  const { get } = useUrlState();
  const { sort, onSort } = useSortState("name");
  const q = useApi<TenantRow[]>("/api/tenants", { q: get("q"), sort: sort.sort, dir: sort.dir });
  const columns: Column<TenantRow>[] = [
    { key: "name", header: "Name", sort: "name", cell: (t) => <span className="fw-semibold">{t.name}</span> },
    { key: "email", header: "Email", sort: "email", cell: (t) => t.email || "—" },
    { key: "phone", header: "Phone", cell: (t) => t.phone || "—" },
    { key: "tenancies", header: "Tenancies", align: "end", cell: (t) => t.tenancyCount },
  ];
  return (
    <>
      <PageHeader
        title="Tenants"
        subtitle="People who rent your properties"
        actions={
          <Can permission="tenancies:write">
            <IconButtonLink to="/tenants/new" icon="plus">
              Add tenant
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar>
          <SearchInput placeholder="Search name, email, phone" />
        </FilterBar>
        <QueryState query={q}>
          {(rows) => (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(t) => t.id}
              rowLink={(t) => `/tenants/${t.id}`}
              sort={sort}
              onSort={onSort}
              empty={
                <EmptyState icon="users" title="No tenants yet">
                  Tenants are usually added when you create a tenancy.
                </EmptyState>
              }
            />
          )}
        </QueryState>
      </Card>
    </>
  );
}

export function TenantFormPage() {
  const { id } = useParams();
  return (
    <EntityForm
      schema={tenantSchema}
      resource="/api/tenants"
      id={id}
      title={id ? "Edit tenant" : "Add tenant"}
      breadcrumbs={[{ label: "Tenants", to: "/tenants" }, { label: id ? "Edit" : "New" }]}
      defaults={{ name: "", email: "", phone: "", notes: "" }}
      toForm={nullsToEmpty}
      redirect={(s) => `/tenants/${s.id}`}
      cancelTo={id ? `/tenants/${id}` : "/tenants"}
    >
      {() => (
        <Row>
          <Col md={6}>
            <TextField name="name" label="Full name" required />
          </Col>
          <Col md={3}>
            <TextField name="email" label="Email" type="email" />
          </Col>
          <Col md={3}>
            <TextField name="phone" label="Phone" type="tel" />
          </Col>
          <Col md={12}>
            <TextAreaField name="notes" label="Notes" />
          </Col>
        </Row>
      )}
    </EntityForm>
  );
}

export function TenantDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<TenantRow>(`/api/tenants/${id}`);
  return (
    <QueryState query={q}>
      {(t) => (
        <>
          <PageHeader
            title={t.name}
            subtitle={[t.email, t.phone].filter(Boolean).join(" · ")}
            breadcrumbs={[{ label: "Tenants", to: "/tenants" }, { label: t.name }]}
            actions={
              <>
                <Can permission="communications:write">
                  <IconButtonLink
                    to={`/communications/new?tenantId=${t.id}`}
                    icon="message"
                    variant="outline-primary"
                  >
                    Log communication
                  </IconButtonLink>
                </Can>
                <Can permission="tenancies:write">
                  <IconButtonLink to={`/tenants/${t.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="tenancies:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this tenant?"
                    body="Tenants on a tenancy can't be deleted."
                    onConfirm={async () => {
                      await api.delete(`/api/tenants/${t.id}`);
                      await invalidate();
                      navigate("/tenants");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Tenancies" flush>
                <Table className="mb-0">
                  <tbody>
                    {(t.tenancies ?? []).map((x) => (
                      <tr key={x.id}>
                        <td>
                          <Link to={`/tenancies/${x.id}`}>
                            {x.addressLine1}, {x.postcode}
                          </Link>
                        </td>
                        <td>
                          {f.date(x.startDate)} – {x.endDate ? f.date(x.endDate) : "present"}
                        </td>
                        <td>
                          <StatusBadge status={x.status} label={f.label(TENANCY_STATUSES, x.status)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
              <Section title="Communications" flush>
                <CommunicationTable filters={{ tenantId: t.id }} />
              </Section>
              {t.notes && (
                <Section title="Notes">
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {t.notes}
                  </p>
                </Section>
              )}
            </Col>
            <Col lg={4}>
              <Attachments entityType="tenant" entityId={t.id} />
              <AuditHistory entityType="tenant" entityId={t.id} />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}

// ------------------------------------------------------------------ owners

interface OwnerRow {
  id: string;
  ownerType: string;
  name: string;
  email: string | null;
  /** Masked hints (#55); the full value only comes from an audited reveal. */
  utrHint: string | null;
  ninoHint: string | null;
  companyNumber: string | null;
  accountingBasis: string;
  mtdStatus: string;
  mtdStartDate: string | null;
  quarterlyPeriodType: string;
  consolidatedExpenses: boolean;
  yearEndMonth: number | null;
  yearEndDay: number | null;
  notes: string;
  propertyCount: number;
  properties?: Array<{
    id: string;
    addressLine1: string;
    postcode: string;
    shareBp: number;
    effectiveFromDate: string;
    effectiveToDate: string | null;
  }>;
}

/** A masked identifier, with a Reveal button for members allowed to see it (audited). */
function Identifier({
  ownerId,
  field,
  hint,
}: {
  ownerId: string;
  field: "utr" | "nino";
  hint: string | null;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!value) return;
    // Hide it again after a minute.
    const t = setTimeout(() => setValue(null), 60_000);
    return () => clearTimeout(t);
  }, [value]);
  if (!hint) return <>—</>;
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span className="font-monospace">{value ?? hint}</span>
      <Can permission="owners:reveal">
        {value ? (
          <Button size="sm" variant="link" className="p-0" onClick={() => setValue(null)}>
            Hide
          </Button>
        ) : (
          <Button
            size="sm"
            variant="link"
            className="p-0"
            onClick={async () => {
              setError(null);
              try {
                setValue(
                  (await api.post<{ value: string }>(`/api/owners/${ownerId}/reveal`, { field })).value,
                );
              } catch (e) {
                setError((e as Error).message);
              }
            }}
          >
            Reveal
          </Button>
        )}
      </Can>
      {error && <span className="text-danger small">{error}</span>}
    </span>
  );
}

export function OwnerListPage() {
  const f = useFormat();
  const q = useApi<OwnerRow[]>("/api/owners");
  const columns: Column<OwnerRow>[] = [
    { key: "name", header: "Name", cell: (o) => <span className="fw-semibold">{o.name}</span> },
    { key: "type", header: "Type", cell: (o) => f.label(OWNER_TYPES, o.ownerType) },
    { key: "basis", header: "Accounting basis", cell: (o) => f.label(ACCOUNTING_BASES, o.accountingBasis) },
    {
      key: "mtd",
      header: "MTD for Income Tax",
      cell: (o) => (o.ownerType === "company" ? "Not applicable" : f.label(MTD_STATUSES, o.mtdStatus)),
    },
    { key: "props", header: "Properties", align: "end", cell: (o) => o.propertyCount },
  ];
  return (
    <>
      <PageHeader
        title="Owners"
        subtitle="Individuals and companies that own your properties — tax figures are reported per owner"
        actions={
          <Can permission="owners:write">
            <IconButtonLink to="/owners/new" icon="plus">
              Add owner
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <QueryState query={q}>
          {(rows) => (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(o) => o.id}
              rowLink={(o) => `/owners/${o.id}`}
              empty={
                <EmptyState
                  icon="owner"
                  title="No owners yet"
                  action={
                    <IconButtonLink to="/owners/new" icon="plus">
                      Add owner
                    </IconButtonLink>
                  }
                >
                  Add the people or companies who own your properties, then set ownership shares on each
                  property. This lets you produce tax figures per owner, including joint ownership.
                </EmptyState>
              }
            />
          )}
        </QueryState>
      </Card>
    </>
  );
}

export function OwnerFormPage() {
  const { id } = useParams();
  return (
    <EntityForm
      schema={taxOwnerSchema}
      checks={[taxOwnerChecks]}
      resource="/api/owners"
      id={id}
      title={id ? "Edit owner" : "Add owner"}
      breadcrumbs={[{ label: "Owners", to: "/owners" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        ownerType: "individual",
        name: "",
        email: "",
        utr: "",
        nino: "",
        companyNumber: "",
        accountingBasis: "cash",
        mtdStatus: "not_signed_up",
        mtdStartDate: "",
        quarterlyPeriodType: "standard",
        consolidatedExpenses: false,
        yearEndMonth: "",
        yearEndDay: "",
        notes: "",
      }}
      toForm={nullsToEmpty}
      redirect={(s) => `/owners/${s.id}`}
      cancelTo={id ? `/owners/${id}` : "/owners"}
    >
      {(form) => {
        const company = form.watch("ownerType") === "company";
        return (
          <Row>
            <Col md={4}>
              <SelectField
                name="ownerType"
                label="Owner type"
                options={OWNER_TYPES}
                required
                onChangeValue={(v) => v === "company" && form.setValue("accountingBasis", "accruals")}
              />
            </Col>
            <Col md={8}>
              <TextField name="name" label={company ? "Company name" : "Full name"} required />
            </Col>
            <Col md={4}>
              <TextField name="email" label="Email" type="email" />
            </Col>
            {company ? (
              <Col md={4}>
                <TextField name="companyNumber" label="Company number" />
              </Col>
            ) : (
              <Col md={4}>
                <TextField
                  name="nino"
                  label="National Insurance number"
                  hint={
                    id
                      ? "Stored encrypted. Leave blank to keep the current one."
                      : "Stored encrypted and shown masked."
                  }
                />
              </Col>
            )}
            <Col md={4}>
              <TextField
                name="utr"
                label="Unique Taxpayer Reference (UTR)"
                hint={
                  id
                    ? "Stored encrypted. Leave blank to keep the current one."
                    : "Stored encrypted and shown masked."
                }
              />
            </Col>
            <Col md={4}>
              <SelectField
                name="accountingBasis"
                label="Accounting basis"
                options={company ? ACCOUNTING_BASES.filter((b) => b.value === "accruals") : ACCOUNTING_BASES}
                hint={
                  company
                    ? "Companies use the accruals basis."
                    : "Cash basis is the default for most individual landlords."
                }
              />
            </Col>
            {company ? (
              <>
                <Col md={4}>
                  <NumberField
                    name="yearEndDay"
                    label="Accounting year end — day"
                    min={1}
                    max={31}
                    required
                  />
                </Col>
                <Col md={4}>
                  <NumberField
                    name="yearEndMonth"
                    label="Accounting year end — month"
                    min={1}
                    max={12}
                    required
                  />
                </Col>
              </>
            ) : (
              <>
                <Col md={4}>
                  <SelectField name="mtdStatus" label="Making Tax Digital status" options={MTD_STATUSES} />
                </Col>
                <Col md={4}>
                  <DateField name="mtdStartDate" label="MTD start date" />
                </Col>
                <Col md={6}>
                  <SelectField
                    name="quarterlyPeriodType"
                    label="Quarterly update periods"
                    options={QUARTERLY_PERIOD_TYPES}
                  />
                </Col>
                <Col md={6} className="pt-md-4">
                  <CheckboxField
                    name="consolidatedExpenses"
                    label="Report consolidated expenses (small landlords)"
                  />
                </Col>
              </>
            )}
            <Col md={12}>
              <TextAreaField name="notes" label="Notes" rows={2} />
            </Col>
          </Row>
        );
      }}
    </EntityForm>
  );
}

export function OwnerDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<OwnerRow>(`/api/owners/${id}`);
  return (
    <QueryState query={q}>
      {(o) => (
        <>
          <PageHeader
            title={o.name}
            subtitle={`${f.label(OWNER_TYPES, o.ownerType)} · ${f.label(ACCOUNTING_BASES, o.accountingBasis)}`}
            breadcrumbs={[{ label: "Owners", to: "/owners" }, { label: o.name }]}
            actions={
              <>
                <Can permission="tax:read">
                  <IconButtonLink to={`/tax/report?ownerId=${o.id}`} icon="percent" variant="outline-primary">
                    Tax report
                  </IconButtonLink>
                </Can>
                {o.ownerType === "individual" && (
                  <Can permission="tax:read">
                    <IconButtonLink to={`/tax/mtd?ownerId=${o.id}`} icon="calendar" variant="outline-primary">
                      MTD quarters
                    </IconButtonLink>
                  </Can>
                )}
                <Can permission="owners:write">
                  <IconButtonLink to={`/owners/${o.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="owners:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this owner?"
                    body="Owners with property shares can't be deleted."
                    onConfirm={async () => {
                      await api.delete(`/api/owners/${o.id}`);
                      await invalidate();
                      navigate("/owners");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Details">
                <DefinitionList
                  items={[
                    ["Email", o.email],
                    o.ownerType === "company"
                      ? ["Company number", o.companyNumber]
                      : [
                          "National Insurance",
                          <Identifier key="nino" ownerId={o.id} field="nino" hint={o.ninoHint} />,
                        ],
                    ["UTR", <Identifier key="utr" ownerId={o.id} field="utr" hint={o.utrHint} />],
                    o.ownerType === "company"
                      ? [
                          "Accounting year end",
                          o.yearEndDay && o.yearEndMonth ? `${o.yearEndDay}/${o.yearEndMonth}` : "—",
                        ]
                      : [
                          "MTD status",
                          `${f.label(MTD_STATUSES, o.mtdStatus)}${o.mtdStartDate ? ` from ${f.date(o.mtdStartDate)}` : ""}`,
                        ],
                    o.ownerType === "individual"
                      ? ["Quarterly periods", f.label(QUARTERLY_PERIOD_TYPES, o.quarterlyPeriodType)]
                      : null,
                    ["Notes", o.notes],
                  ]}
                />
              </Section>
              <Section title="Property shares" flush>
                {(o.properties ?? []).length === 0 ? (
                  <p className="small text-muted p-3 mb-0">
                    No property shares. Set ownership from a property's Ownership tab.
                  </p>
                ) : (
                  <Table className="mb-0">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th className="text-end">Share</th>
                        <th>From</th>
                        <th>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.properties!.map((p) => (
                        <tr key={`${p.id}-${p.effectiveFromDate}`}>
                          <td>
                            <Link to={`/properties/${p.id}?tab=ownership`}>
                              {p.addressLine1}, {p.postcode}
                            </Link>
                          </td>
                          <td className="text-end">{percent(p.shareBp)}</td>
                          <td>{f.date(p.effectiveFromDate)}</td>
                          <td>{p.effectiveToDate ? f.date(p.effectiveToDate) : "Present"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
            </Col>
            <Col lg={4}>
              <Attachments entityType="tax_owner" entityId={o.id} />
              <AuditHistory entityType="tax_owner" entityId={o.id} />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}

// ------------------------------------------------------------------ contractors

interface ContractorRow {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  services: string[];
  notes: string;
  isActive: boolean;
  totalSpendPence: number;
  jobCount: number;
  properties?: Array<{ id: string; addressLine1: string; postcode: string }>;
}

export function ContractorListPage() {
  const { get } = useUrlState();
  const { sort, onSort } = useSortState("name");
  const q = useApi<ContractorRow[]>("/api/contractors", {
    q: get("q"),
    sort: sort.sort,
    dir: sort.dir,
    includeInactive: get("inactive") === "1",
  });
  const columns: Column<ContractorRow>[] = [
    {
      key: "name",
      header: "Name",
      sort: "name",
      cell: (c) => (
        <>
          <span className="fw-semibold">{c.name}</span>
          {!c.isActive && <StatusBadge status="archived" label="inactive" />}
        </>
      ),
    },
    { key: "company", header: "Company", sort: "company", cell: (c) => c.company || "—" },
    { key: "services", header: "Services", cell: (c) => c.services.join(", ") || "—" },
    { key: "contact", header: "Contact", cell: (c) => [c.phone, c.email].filter(Boolean).join(" · ") || "—" },
    {
      key: "spend",
      header: "Total spend",
      sort: "spend",
      align: "end",
      cell: (c) => <Money pence={c.totalSpendPence} kind="expense" />,
    },
  ];
  return (
    <>
      <PageHeader
        title="Contractors & suppliers"
        subtitle="Trades and suppliers you use"
        actions={
          <Can permission="contractors:write">
            <IconButtonLink to="/contractors/new" icon="plus">
              Add contractor
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar>
          <SearchInput placeholder="Search name, company, service" />
        </FilterBar>
        <QueryState query={q}>
          {(rows) => (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(c) => c.id}
              rowLink={(c) => `/contractors/${c.id}`}
              sort={sort}
              onSort={onSort}
              empty={
                <EmptyState
                  icon="briefcase"
                  title="No contractors yet"
                  action={
                    <IconButtonLink to="/contractors/new" icon="plus">
                      Add contractor
                    </IconButtonLink>
                  }
                >
                  Add plumbers, electricians, agents and suppliers so you can select them on expenses and
                  maintenance.
                </EmptyState>
              }
            />
          )}
        </QueryState>
      </Card>
    </>
  );
}

export function ContractorFormPage() {
  const { id } = useParams();
  return (
    <EntityForm
      schema={contractorSchema}
      resource="/api/contractors"
      id={id}
      title={id ? "Edit contractor" : "Add contractor"}
      breadcrumbs={[{ label: "Contractors", to: "/contractors" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        name: "",
        company: "",
        email: "",
        phone: "",
        address: "",
        services: [],
        notes: "",
        isActive: true,
      }}
      toForm={nullsToEmpty}
      redirect={(s) => `/contractors/${s.id}`}
      cancelTo={id ? `/contractors/${id}` : "/contractors"}
    >
      {(form) => (
        <Row>
          <Col md={6}>
            <TextField name="name" label="Name" required />
          </Col>
          <Col md={6}>
            <TextField name="company" label="Company" />
          </Col>
          <Col md={4}>
            <TextField name="phone" label="Phone" type="tel" />
          </Col>
          <Col md={4}>
            <TextField name="email" label="Email" type="email" />
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3" controlId="services">
              <Form.Label>Services</Form.Label>
              <Form.Control
                defaultValue={(form.getValues("services") as string[]).join(", ")}
                placeholder="e.g. plumbing, gas safe"
                onChange={(e) =>
                  form.setValue(
                    "services",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
              <Form.Text className="text-muted">Comma-separated.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={12}>
            <TextAreaField name="address" label="Address" rows={2} />
          </Col>
          <Col md={12}>
            <TextAreaField name="notes" label="Notes" rows={2} />
          </Col>
          <Col md={12}>
            <CheckboxField name="isActive" label="Active (show in selection lists)" />
          </Col>
        </Row>
      )}
    </EntityForm>
  );
}

export function ContractorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<ContractorRow>(`/api/contractors/${id}`);
  return (
    <QueryState query={q}>
      {(c) => (
        <>
          <PageHeader
            title={c.name}
            subtitle={[c.company, c.services.join(", ")].filter(Boolean).join(" · ")}
            breadcrumbs={[{ label: "Contractors", to: "/contractors" }, { label: c.name }]}
            actions={
              <>
                <Can permission="expenses:write">
                  <IconButtonLink
                    to={`/expenses/new?contractorId=${c.id}`}
                    icon="plus"
                    variant="outline-primary"
                  >
                    Add expense
                  </IconButtonLink>
                </Can>
                <Can permission="contractors:write">
                  <IconButtonLink to={`/contractors/${c.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="contractors:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this contractor?"
                    body="Past expenses and maintenance keep their history."
                    onConfirm={async () => {
                      await api.delete(`/api/contractors/${c.id}`);
                      await invalidate();
                      navigate("/contractors");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Contact">
                <DefinitionList
                  items={[
                    ["Phone", c.phone],
                    ["Email", c.email],
                    ["Address", c.address],
                    ["Total spend", <Money key="s" pence={c.totalSpendPence} kind="expense" />],
                    ["Notes", c.notes],
                  ]}
                />
              </Section>
              <Section title="Expenses" flush>
                <ExpenseTable filters={{ contractorId: c.id }} />
              </Section>
              <Section title="Maintenance jobs" flush>
                <MaintenanceTable filters={{ contractorId: c.id }} />
              </Section>
            </Col>
            <Col lg={4}>
              <Section title="Properties worked on">
                {(c.properties ?? []).length === 0 ? (
                  <p className="small text-muted mb-0">None yet.</p>
                ) : (
                  <ul className="mb-0">
                    {c.properties!.map((p) => (
                      <li key={p.id}>
                        <Link to={`/properties/${p.id}`}>
                          {p.addressLine1}, {p.postcode}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
              <Attachments
                entityType="contractor"
                entityId={c.id}
                title="Documents (insurance, certifications)"
              />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
